import React, { useState, useCallback, useMemo, Fragment } from "react";
import styled from "styled-components";
import arrayMove from "array-move";

import { State } from "src/state/ducks";
import {
  BusinessTimeInDays,
  initialBusinessTimeInDays,
  shopInitialEntity,
} from "src/firebase/db/Shops";

import StyleConfig from "src/constants/StyleConfig";

import Button from "src/components/atoms/button/Button";
import HomeButton from "src/components/atoms/button/HomeButton";
import HashTagButton from "src/components/atoms/button/HashTagButton";
import SelectBox from "src/components/atoms/form/SelectBox";
import InputForm from "src/components/atoms/form/InputForm";
import { ValidateMessageIfError } from "src/components/atoms/form/ErrorMessage";
import InputLabelHolder from "src/components/molecules/form/InputLabelHolder";
import SingleInputFormBlock from "src/components/molecules/form/SingleInputFormBlock";
import DoubleInputFormBlock from "src/components/molecules/form/DoubleInputFormBlock";
import RadioInputBlock from "src/components/molecules/form/RadioInputBlock";
import BusinessDayMaker, {
  HolidayMarker,
  BusinessTime,
} from "src/components/organisms/form/BusinessDayMaker";
import UploadImageBlock from "src/components/organisms/form/UploadImageBlock";

import { PrefectureCode } from "src/constants/PrefectureCode";
import { PublishStatus4Option } from "src/constants/StatusCode";
import { useEffect } from "react";
import Heading from "src/components/atoms/typo/Heading";
import AnnotaionText from "src/components/atoms/form/AnnotationText";
import CheckBox from "src/components/atoms/form/CheckBox";
import MyDatePicker from "src/components/atoms/form/MyDatePicker";
import { UserRole } from "src/firebase/db/Admins";
import { formatDateToDate, toaster } from "src/helpers/UtilityHelper";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(isSameOrAfter);

type Tag = State["master"]["tags"][number];
type Shop = State["shop"]["resource"][number];
type Props = {
  formType: "CREATE" | "UPDATE";
  shop: Shop;
  tags: Tag[];
  hasNotifications: boolean;
  errors: State["shop"]["validationErrors"];
  onSubmit: (query: Shop) => void;
  role?: UserRole;
};

// 参照元：https://github.com/madefor/postal-code-api
type AddressResponseProps = {
  code: string;
  data: {
    prefcode: string;
    ja: {
      prefecture: string;
      address1: string;
      address2: string;
      address3: string;
      address4: string;
    };
  }[];
};

const mapEntityToState = (entity: Shop) => {
  const businessTimeInDays = Object.entries(entity)
    .filter(([key]) => key.match(/in_business$/))
    .reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {} as BusinessTimeInDays
    );
  return {
    address: entity.address,
    addressNumber: entity.address_number,
    city: entity.city,
    facebookUrl: entity.facebook_url,
    hasEatin: entity.has_eatin ? "有り" : "無し",
    images: entity.images,
    instagramUrl: entity.instagram_url,
    isDeleted: entity.is_deleted,
    kana: entity.kana,
    name: entity.name,
    otherHoliday: entity.other_holiday,
    phone: entity.phone,
    postalCodeForward: entity.postal_code.substring(0, 3),
    postalCodeBackward: entity.postal_code.substring(3),
    prefectureId: entity.prefecture_id,
    publishStatus: entity.publish_status,
    shopUrl: entity.shop_url,
    twitterUrl: entity.twitter_url,
    lineUrl: entity.line_url || "",
    releasedTime: entity.released_time
      ? formatDateToDate(entity.released_time)
      : undefined,
    businessTimes: businessTimeInDaysToBusinessTimes(businessTimeInDays),
    holidays: businessTimeInDaysToHolidays(businessTimeInDays),
    hashTags: entity.tags,
    note_distributor: entity.note_distributor,
    note_contact: entity.note_contact,
    note_responsible_person: entity.note_responsible_person,
  };
};

const mapStateToEntity = (
  prevEntity: Shop,
  state: ReturnType<typeof mapEntityToState>
): Shop => ({
  ...prevEntity,
  address: state.address,
  address_number: state.addressNumber,
  city: state.city,
  facebook_url: state.facebookUrl,
  has_eatin: state.hasEatin === "有り",
  images: state.images,
  instagram_url: state.instagramUrl,
  is_deleted: state.isDeleted,
  kana: state.kana,
  line_url: state.lineUrl,
  name: state.name,
  other_holiday: state.otherHoliday,
  phone: state.phone,
  postal_code: state.postalCodeForward + state.postalCodeBackward,
  prefecture_id: state.prefectureId,
  publish_status: state.publishStatus,
  shop_url: state.shopUrl,
  twitter_url: state.twitterUrl,
  released_time: state.releasedTime,
  tags: state.hashTags,
  note_distributor: state.note_distributor,
  note_contact: state.note_contact,
  note_responsible_person: state.note_responsible_person,
  ...businessTimesToBusinessTimeInDays(state.businessTimes, state.holidays),
});

const ShopForm = (props: Props) => {
  const { formType, shop, tags, hasNotifications, errors, onSubmit } = props;
  const { released_time } = shop;

  const [state, setState] = useState(mapEntityToState(shopInitialEntity));

  const {
    address,
    addressNumber,
    city,
    facebookUrl,
    hasEatin,
    images,
    instagramUrl,
    kana,
    name,
    otherHoliday,
    phone,
    postalCodeForward,
    postalCodeBackward,
    prefectureId,
    publishStatus,
    shopUrl,
    twitterUrl,
    lineUrl,
    releasedTime,
    businessTimes,
    holidays,
    note_distributor,
    note_contact,
    note_responsible_person,
  } = state;

  const tomorrow = useMemo(
    () => dayjs().add(1, "day").hour(0).minute(0).second(0).toDate(),
    []
  );

  useEffect(() => {
    setState(mapEntityToState(shop));
  }, [shop]);

  const HashTags = () => (
    <ul className="hashtag">
      {tags
        .filter((tag) => tag.is_published)
        .map((tag) => {
          const id = `tags/${tag.id}`;
          const isActive = state.hashTags.includes(id);
          const handleChange = () => {
            setState((prev) => ({
              ...prev,
              hashTags: isActive
                ? prev.hashTags.filter((hashTag) => hashTag !== id)
                : [...prev.hashTags, id],
            }));
          };
          return (
            <li key={id}>
              <HashTagButton
                isActive={isActive}
                text={`# ${tag.name}`}
                onClick={handleChange}
              />
            </li>
          );
        })}
    </ul>
  );

  const hasPostalCode = useMemo(() => postalCodeForward && postalCodeBackward, [
    postalCodeForward,
    postalCodeBackward,
  ]);

  const onSearchAddress = useCallback(async () => {
    try {
      if (!hasPostalCode) {
        return;
      }
      const res: AddressResponseProps = await fetch(
        `https://madefor.github.io/postal-code-api/api/v1/${postalCodeForward}/${postalCodeBackward}.json`,
        { mode: "cors" }
      )
        .then((res) => {
          return res.json();
        })
        .catch((err) => {
          toaster(
            "error",
            `${postalCodeForward}-${postalCodeBackward}は存在しない郵便番号です。`
          );
          throw err;
        });
      const { data } = res;
      if (!data || !data.length) {
        toaster(
          "error",
          `${postalCodeForward}-${postalCodeBackward}は存在しない郵便番号です。`
        );
        return;
      }
      const { prefecture, address1, address2, address3, address4 } = data[0][
        "ja"
      ];
      const prefObj = PrefectureCode.find(
        ({ content }) => content === prefecture
      );
      const allAddress = `${address2}${address3}${address4}`;
      setState((prev) => ({
        ...prev,
        prefectureId: prefObj ? prefObj.value : prev.prefectureId,
        city: address1 || prev.city,
        address: allAddress || prev.address,
      }));
    } catch (err) {
      console.error(err);
    }
  }, [hasPostalCode, postalCodeForward, postalCodeBackward, setState]);

  const handleSubmit = () => {
    onSubmit(mapStateToEntity(shop, state));
  };

  const isDisabledReleasedTime = useMemo(() => {
    // 新規作成の場合はそのまま入力させる
    if (hasNotifications) {
      return true;
    }

    if (!released_time) {
      return false;
    }

    return dayjs().isSameOrAfter(formatDateToDate(released_time));
  }, [hasNotifications, released_time]);

  const onClickPrevThumbnail = useCallback(
    (i: number) => {
      setState((prev) => {
        const orderedImages = arrayMove(prev.images, i, i - 1);
        return {
          ...prev,
          images: orderedImages,
        };
      });
    },
    [setState]
  );

  const onClickNextThumbnail = useCallback(
    (i: number) => {
      setState((prev) => {
        const orderedImages = arrayMove(prev.images, i, i + 1);
        return {
          ...prev,
          images: orderedImages,
        };
      });
    },
    [setState]
  );

  return (
    <Form>
      <div className="item">
        <UploadImageBlock
          label="店舗画像"
          thumbnails={images}
          multiple
          onChange={(images) => setState((prev) => ({ ...prev, images }))}
          onClickPrev={onClickPrevThumbnail}
          onClickNext={onClickNextThumbnail}
        />
      </div>
      {formType === "UPDATE" ? (
        <div className="item">
          <SingleInputFormBlock
            isRequired
            labelName="ID"
            inputType="text"
            inputValue={shop.id}
            onChange={() => {}}
            placeholder="店舗ID"
            isError={false}
            isDisabeld
          />
        </div>
      ) : null}
      <div className="item">
        <SingleInputFormBlock
          isRequired
          labelName="店名"
          inputType="text"
          inputValue={name}
          onChange={(name) => setState((prev) => ({ ...prev, name }))}
          placeholder="御店名を入力してください"
          isError={false}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.name} />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired
          labelName="フリガナ"
          inputType="text"
          inputValue={kana}
          onChange={(kana) => setState((prev) => ({ ...prev, kana }))}
          placeholder="フリガナを入力してください"
          isError={false}
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.kana} />
      </div>
      <div className="item item-zipcode">
        <div className="item-zipcode-inner">
          <DoubleInputFormBlock
            isRequired
            labelName="郵便番号"
            inputType="text"
            inputValue={postalCodeForward}
            onChange={(postalCodeForward) =>
              setState((prev) => ({ ...prev, postalCodeForward }))
            }
            placeholder="100"
            isError={false}
            inputType2="text"
            inputValue2={postalCodeBackward}
            onChange2={(postalCodeBackward) =>
              setState((prev) => ({ ...prev, postalCodeBackward }))
            }
            placeholder2="0000"
            isError2={false}
            hasSearch
            disabledSearch={!hasPostalCode}
            onSearch={onSearchAddress}
          />
        </div>
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.postal_code}
        />
      </div>
      <div className="item">
        <InputLabelHolder labelName="住所" isRequired />
        <SelectBox
          id="prefecture"
          inputValue={prefectureId}
          onChange={(prefectureId) =>
            setState((prev) => ({ ...prev, prefectureId }))
          }
          options={PrefectureCode}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.prefecture_id}
        />
        <div className="mt8">
          <InputForm
            inputType="text"
            inputValue={city}
            placeholder="千代田区"
            onChange={(city) => setState((prev) => ({ ...prev, city }))}
            isError={false}
          />
          <ValidateMessageIfError
            marginVertical={8}
            messages={props.errors.city}
          />
        </div>
        <div className="mt8">
          <InputForm
            inputType="text"
            inputValue={address}
            placeholder="有楽町1-10-1"
            onChange={(address) => setState((prev) => ({ ...prev, address }))}
            isError={false}
          />
          <ValidateMessageIfError
            marginVertical={8}
            messages={props.errors.address}
          />
        </div>
        <div className="mt8">
          <InputForm
            inputType="text"
            inputValue={addressNumber}
            placeholder="ビル名等"
            onChange={(addressNumber) =>
              setState((prev) => ({ ...prev, addressNumber }))
            }
            isError={false}
          />
          <ValidateMessageIfError
            marginVertical={8}
            messages={props.errors.address_number}
          />
        </div>
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired
          labelName="電話番号（ハイフン無し）"
          inputType="text"
          inputValue={phone}
          onChange={(phone) => setState((prev) => ({ ...prev, phone }))}
          placeholder="電話番号を入力してください"
          isError={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.phone}
        />
      </div>
      <div className="item">
        <RadioInputBlock
          isRequired
          labelName="イートインスペースの有無"
          id="has_eatin"
          name="has_eatin"
          value={hasEatin}
          onValue="有り"
          offValue="無し"
          onChange={(hasEatin) => setState((prev) => ({ ...prev, hasEatin }))}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.has_eatin}
        />
      </div>
      <div className="item">
        <BusinessDayMaker
          businessTimes={businessTimes}
          holidays={holidays}
          onChange={(businessTimes) =>
            setState((prev) => ({ ...prev, businessTimes }))
          }
        />
      </div>
      <div className="item">
        <HolidayMarker
          holidays={holidays}
          businessDays={businessTimes.map(({ days }) => days).flat()}
          onChange={(holidays) => setState((prev) => ({ ...prev, holidays }))}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.in_business}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired={false}
          labelName="その他特別なお休み"
          inputType="text"
          inputValue={otherHoliday}
          onChange={(otherHoliday) =>
            setState((prev) => ({ ...prev, otherHoliday }))
          }
          placeholder="その他休みがあれば入力してください"
          isError={false}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired={false}
          labelName="お店のホームページのURL"
          inputType="text"
          inputValue={shopUrl}
          onChange={(shopUrl) => setState((prev) => ({ ...prev, shopUrl }))}
          placeholder="https://sacri.jp"
          isError={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.shop_url}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired={false}
          labelName="お店のtwitterのURL"
          inputType="text"
          inputValue={twitterUrl}
          onChange={(twitterUrl) =>
            setState((prev) => ({ ...prev, twitterUrl }))
          }
          placeholder="https://twitter.com/"
          isError={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.twitter_url}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired={false}
          labelName="お店のinstagramのURL"
          inputType="text"
          inputValue={instagramUrl}
          onChange={(instagramUrl) =>
            setState((prev) => ({ ...prev, instagramUrl }))
          }
          placeholder="http://instagram.com/"
          isError={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.instagram_url}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired={false}
          labelName="お店のfacebookのURL"
          inputType="text"
          inputValue={facebookUrl}
          onChange={(facebookUrl) =>
            setState((prev) => ({ ...prev, facebookUrl }))
          }
          placeholder="http://facebook.com/"
          isError={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.facebook_url}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired={false}
          labelName="お店のLINEアカウントのURL"
          inputType="text"
          inputValue={lineUrl}
          onChange={(lineUrl) => setState((prev) => ({ ...prev, lineUrl }))}
          placeholder="https://line.me/R/ti/p/XXXXXXX"
          isError={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={props.errors.facebook_url}
        />
      </div>
      <div className="item">{HashTags()}</div>

      <Heading tag="h2" text="特定商取引法に関する記載" />
      <div className="item">
        <SingleInputFormBlock
          isRequired
          labelName="販売業者の氏名または名称"
          inputType="text"
          inputValue={note_distributor}
          onChange={(note_distributor) =>
            setState((prev) => ({ ...prev, note_distributor }))
          }
          placeholder="株式会社◯◯"
          isError={false}
        />
        <AnnotaionText
          text={` ※法人の場合は、【株式会社◯◯／合同会社◯◯】\n個人事業主、屋号ありの場合は【◯◯ベーカリー　◯◯花子】と入れてください`}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.note_distributor}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired
          labelName="販売業者の住所および連絡先"
          inputType="text"
          inputValue={note_contact}
          onChange={(note_contact) =>
            setState((prev) => ({ ...prev, note_contact }))
          }
          placeholder="東京都渋谷区千駄ヶ谷x−x−x"
          isError={false}
        />
        <AnnotaionText text={`※住所もしくは、電話番号を入力してください`} />
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.note_contact}
        />
      </div>
      <div className="item">
        <SingleInputFormBlock
          isRequired
          labelName="販売業者の代表者または販売責任者"
          inputType="text"
          inputValue={note_responsible_person}
          onChange={(note_responsible_person) =>
            setState((prev) => ({ ...prev, note_responsible_person }))
          }
          placeholder="代表取締役 ◯◯太郎"
          isError={false}
        />
        <AnnotaionText
          text={`※【代表取締役 ◯◯太郎】や\n【販売責任者 ◯◯花子】等で記入してください`}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.note_responsible_person}
        />
      </div>

      <Heading tag="h2" text="初回の焼き上がり配信開始日" />
      <div className="item day-picker">
        <MyDatePicker
          className="datepicker"
          date={releasedTime}
          placeholder={isDisabledReleasedTime ? "既に配信済み" : "日付を選択"}
          dateFormat="yyyy年MM月dd日(EEEEE)"
          onChange={(releasedTime) =>
            setState((prev) => ({ ...prev, releasedTime }))
          }
          isDisabled={isDisabledReleasedTime}
          showTimeSelect={false}
          minDate={tomorrow}
        />
      </div>

      {props.role === UserRole.ADMIN ? (
        <Fragment>
          <Heading tag="h2" text="sacriの管理者限定" />
          <div className="item">
            <InputLabelHolder labelName="公開状況" isRequired />
            <SelectBox
              id="publish_staus"
              inputValue={publishStatus}
              onChange={(publishStatus) =>
                setState((prev) => ({ ...prev, publishStatus }))
              }
              options={PublishStatus4Option}
            />
          </div>
          {formType === "UPDATE" ? (
            <div className="item">
              <div className="input_block">
                <CheckBox
                  children={"このお店を削除する"}
                  isChecked={state.isDeleted}
                  onChange={() => {
                    const isDeleted = !state.isDeleted;
                    setState((prev) => ({ ...prev, isDeleted }));
                  }}
                />
                <AnnotaionText
                  text={"削除したデータは元に戻すことができません"}
                />
              </div>
              <ValidateMessageIfError
                marginVertical={8}
                messages={props.errors.is_recommended}
              />
            </div>
          ) : null}
        </Fragment>
      ) : null}
      <div className="submit">
        <div className="submit-item">
          <Button
            type="button"
            isDisabled={false}
            clickEvent={handleSubmit}
            text="保存する"
          />
        </div>
        <div className="submit-item mt20">
          <HomeButton />
        </div>
      </div>
    </Form>
  );
};

const Form = styled.form`
  > .item {
    margin: ${StyleConfig.HELPER.M24} auto;
  }
  .mt8 {
    margin-top: ${StyleConfig.HELPER.M8};
  }
  > .submit {
    padding: ${StyleConfig.HELPER.M48} 0;
    .mt20 {
      margin-top: ${StyleConfig.HELPER.M20};
    }
  }
  .hashtag {
    > li {
      display: inline-block;
      margin: 0 ${StyleConfig.HELPER.M16} ${StyleConfig.HELPER.M16} 0;
    }
  }

  .day-picker {
    display: flex;
    justify-content: space-between;

    ${StyleConfig.MEDIAQUERY.lessThan("small")`
      flex-wrap: wrap;
      justify-content: center;
      background-color: #fff;
      width: 100%;
    `};

    .datepicker {
      border: 2px solid #d2d2d2;
    }

    .react-datepicker {
      width: 300px;
      display: flex;

      .react-datepicker__month-container {
        flex: 2;
      }

      .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {
        right: 95px;
      }
    }
  }
`;

const businessTimeInDaysToBusinessTimes = (
  businessTimeInDays: BusinessTimeInDays
): BusinessTime[] => {
  return Object.entries(businessTimeInDays)
    .map(([key, value]) => ({
      day: key.replace("_in_business", ""),
      ...value,
    }))
    .reduce<BusinessTime[]>((acc, v) => {
      if (!v.open_time && !v.close_time) return acc;
      const isExistIndex = acc.findIndex(
        (a) => a.openingTime === v.open_time && a.closingTime === v.close_time
      );
      return isExistIndex === -1
        ? [
            ...acc,
            {
              days: [v.day],
              openingTime: v.open_time,
              closingTime: v.close_time,
            },
          ]
        : acc.map((a, i) => {
            return i === isExistIndex
              ? {
                  ...a,
                  days: [...a.days, v.day],
                }
              : a;
          });
    }, []);
};

const businessTimesToBusinessTimeInDays = (
  businessTimes: BusinessTime[],
  holidays: string[]
): BusinessTimeInDays => {
  return {
    ...Object.entries(initialBusinessTimeInDays).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          ...value,
          is_holiday: holidays.includes(key.replace("_in_business", "")),
        },
      }),
      {} as BusinessTimeInDays
    ),
    ...businessTimes.reduce((acc, businessTime) => {
      return {
        ...acc,
        ...businessTime.days.reduce((acc, day) => {
          return {
            ...acc,
            [`${day}_in_business`]: {
              open_time: businessTime.openingTime,
              close_time: businessTime.closingTime,
              is_holiday: false,
            },
          };
        }, {} as BusinessTimeInDays),
      };
    }, {} as BusinessTimeInDays),
  };
};

const businessTimeInDaysToHolidays = (
  businessTimeInDays: BusinessTimeInDays
) => {
  return Object.entries(businessTimeInDays)
    .map(([key, value]) => ({
      day: key.replace("_in_business", ""),
      ...value,
    }))
    .filter((v) => v.is_holiday)
    .map((v) => v.day);
};

export default ShopForm;
